import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { VaFileInputMultiple } from '@department-of-veterans-affairs/component-library/dist/react-bindings';

import environment from 'platform/utilities/environment';
import { MISSING_FILE } from 'platform/forms-system/src/js/validation';
import {
  useFileUpload,
  getFileError,
  simulateUploadMultiple,
} from 'platform/forms-system/src/js/web-component-fields/vaFileInputFieldHelpers';
import vaFileInputFieldMapping from 'platform/forms-system/src/js/web-component-fields/vaFileInputFieldMapping';
import { errorManager } from 'platform/forms-system/src/js/utilities/file/passwordErrorState';

import {
  ensureTrackingKey,
  applyCaveStateToFileEntry,
} from '../domain/fileTracking';
import { selectCaveEnabled } from '../domain/selectors';
import { useSecondaryUpload } from '../hooks/useSecondaryUpload';

const DualFileUploadField = props => {
  const { uiOptions = {}, childrenProps } = props;
  const [errors, setErrors] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [percentsUploaded, setPercentsUploaded] = useState([]);

  const dispatch = useDispatch();
  const primaryUploadsRef = useRef({});
  const componentRef = useRef(null);

  const caveEnabled = useSelector(selectCaveEnabled);

  const mappedProps = vaFileInputFieldMapping(props);

  const { percentUploaded, handleUpload } = useFileUpload(
    {
      fileUploadUrl: uiOptions.fileUploadUrl,
      formNumber: uiOptions.formNumber,
      createPayload: uiOptions.createPayload,
      parseResponse: uiOptions.parseResponse,
    },
    mappedProps.accept,
    dispatch,
  );

  const {
    secondaryUploads,
    clearSecondaryTrackingState,
    startOrReuseSecondaryUpload,
  } = useSecondaryUpload({
    formData: childrenProps.formData,
    onChange: childrenProps.onChange,
    caveEnabled,
  });

  useEffect(() => {
    const doPrefill =
      Array.isArray(mappedProps.uploadedFiles) && componentRef.current;
    if (doPrefill) {
      const nulls = new Array(childrenProps.formData.length).fill(null);
      setErrors([...nulls]);
      setCurrentIndex(childrenProps.formData.length - 1);
      componentRef.current.value = mappedProps.uploadedFiles;
    }
    // Run once on mount for prefill only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(
    () => {
      const _percents = [...percentsUploaded];
      _percents[currentIndex] = percentUploaded;
      setPercentsUploaded(_percents);
    },
    // currentIndex/percentsUploaded are derived; avoid feedback loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [percentUploaded],
  );

  useEffect(
    () => {
      if (mappedProps.error === MISSING_FILE) {
        const _errors = [...errors];
        _errors[0] =
          childrenProps?.uiSchema?.['ui:errorMessages']?.required ||
          MISSING_FILE;
        setErrors(_errors);
      }
    },
    // Only react to mappedProps.error; errors/childrenProps would cause unnecessary resets
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mappedProps.error],
  );

  function assignFileUploadToStore(uploadedFile, index) {
    if (!uploadedFile) return;

    const { file, ...rest } = uploadedFile;

    const { name, size, type, lastModified } = file;
    const trackingKey = file?.__sbTrackingKey;
    const secondaryInfo = trackingKey ? secondaryUploads[trackingKey] : null;

    const newFile = applyCaveStateToFileEntry(
      {
        ...rest,
        name,
        size,
        type,
        lastModified,
        idpTrackingKey: trackingKey,
        idpUploadStatus: secondaryInfo?.status || 'pending',
      },
      secondaryInfo,
    );

    const existingFile = childrenProps.formData[index];
    let files;
    if (existingFile) {
      files = [...childrenProps.formData];
      files[index] = newFile;
    } else {
      files = [...childrenProps.formData, newFile];
    }
    childrenProps.onChange(files);
  }

  const handleFileProcessing = (uploadedFile, index) => {
    if (!uploadedFile || !uploadedFile.file) return;

    const _errors = [...errors];
    const _error = uploadedFile.errorMessage || null;
    _errors[index] = _error;
    setErrors(_errors);
    assignFileUploadToStore(uploadedFile, index);

    const trackingKey = uploadedFile.file?.__sbTrackingKey;
    if (trackingKey) {
      primaryUploadsRef.current[trackingKey] = _error ? 'error' : 'done';
    }
  };

  const clearTrackingState = trackingKey => {
    if (!trackingKey) {
      return;
    }

    clearSecondaryTrackingState(trackingKey);
    delete primaryUploadsRef.current[trackingKey];
  };

  const handleFileAdded = async (file, index, mockFormData) => {
    const { fileError } = await getFileError(
      file,
      uiOptions,
      childrenProps.formData || [],
    );
    const _errors = [...errors];

    if (fileError) {
      _errors[index] = fileError;
      setErrors(_errors);
      errorManager.setFileCheckError(index, true);
      return;
    }

    errorManager.setFileCheckError(index, false);
    _errors[index] = null;
    setErrors(_errors);

    const previousTrackingKey = childrenProps.formData?.[index]?.idpTrackingKey;
    const trackingKey = ensureTrackingKey(file);

    if (previousTrackingKey && previousTrackingKey !== trackingKey) {
      clearTrackingState(previousTrackingKey);
    }

    if (environment.isTest() && !environment.isUnitTest()) {
      childrenProps.onChange([mockFormData]);
      return;
    }

    startOrReuseSecondaryUpload(file, trackingKey);

    if (uiOptions.skipUpload) {
      simulateUploadMultiple(
        setPercentsUploaded,
        percentsUploaded,
        index,
        childrenProps,
        file,
      );
      return;
    }

    const primaryStatus = trackingKey
      ? primaryUploadsRef.current[trackingKey]
      : null;
    const shouldStartPrimary = !primaryStatus || primaryStatus === 'error';

    if (shouldStartPrimary) {
      if (trackingKey) {
        primaryUploadsRef.current[trackingKey] = 'pending';
      }
      handleUpload(file, handleFileProcessing, null, index);
    }
  };

  function removeOneFromArray(array, index) {
    return [...array].toSpliced(index, 1);
  }

  const handleFileRemoved = (_file, wcIndex) => {
    const index = (childrenProps.formData || []).findIndex(
      file => file.name === _file.name && file.size === _file.size,
    );

    const trackingKey = childrenProps.formData?.[index]?.idpTrackingKey;
    clearTrackingState(trackingKey);

    const errorIdx = wcIndex ?? index;
    errorManager.removeFileCheckError(errorIdx);
    errorManager.removeInternalFileInputError(errorIdx);

    setErrors(removeOneFromArray(errors, errorIdx));
    setPercentsUploaded(removeOneFromArray(percentsUploaded, errorIdx));

    if (index >= 0) {
      const formData = removeOneFromArray(childrenProps.formData, index);
      childrenProps.onChange(formData);
    }
  };

  const handleChange = e => {
    const { detail } = e;
    const { action, state, file, index: wcIndex, mockFormData } = detail;
    const findFileIndex = (_state, _file) => {
      return _state.findIndex(
        f => f.file.name === _file.name && f.file.size === _file.size,
      );
    };
    switch (action) {
      case 'FILE_ADDED': {
        const _currentIndex = state.length - 1;
        errorManager.setInternalFileInputErrors(_currentIndex, false);
        handleFileAdded(file, _currentIndex, mockFormData);
        setCurrentIndex(_currentIndex);
        break;
      }
      case 'FILE_UPDATED': {
        const idx = findFileIndex(state, file);
        handleFileAdded(file, idx);
        setCurrentIndex(idx);
        break;
      }
      case 'FILE_REMOVED':
        handleFileRemoved(file, wcIndex);
        break;
      default:
        break;
    }
  };

  function getFileInputInstanceIndex(e) {
    const [vaFileInput] = e
      .composedPath()
      .filter(el => el.tagName === 'VA-FILE-INPUT');

    let els = [];
    if (componentRef.current?.shadowRoot) {
      els = Array.from(
        componentRef.current.shadowRoot.querySelectorAll('va-file-input'),
      );
    }
    return els.findIndex(el => el.id === vaFileInput.id);
  }

  const handleInternalFileInputError = e => {
    const index = getFileInputInstanceIndex(e);
    errorManager.setInternalFileInputErrors(index, true);
    const _errors = [...errors];
    _errors[index] = e.detail.error;
    setErrors(_errors);
  };

  return (
    <VaFileInputMultiple
      {...mappedProps}
      error={mappedProps.error}
      ref={componentRef}
      onVaMultipleChange={handleChange}
      onVaFileInputError={handleInternalFileInputError}
      errors={errors}
      percentUploaded={percentsUploaded}
      maxFileSize={uiOptions.maxFileSize}
      minFileSize={uiOptions.minFileSize}
      className="dd-privacy-mask"
      data-dd-action-name="Document Upload"
    />
  );
};

DualFileUploadField.propTypes = {
  childrenProps: PropTypes.shape({
    formData: PropTypes.array,
    onChange: PropTypes.func.isRequired,
    onBlur: PropTypes.func,
    schema: PropTypes.object,
    idSchema: PropTypes.object,
    uiSchema: PropTypes.object,
  }).isRequired,
  error: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  required: PropTypes.bool,
  textDescription: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  uiOptions: PropTypes.shape({
    fileUploadUrl: PropTypes.string,
    formNumber: PropTypes.string,
    createPayload: PropTypes.func,
    parseResponse: PropTypes.func,
    maxFileSize: PropTypes.number,
    minFileSize: PropTypes.number,
    disallowEncryptedPdfs: PropTypes.bool,
    skipUpload: PropTypes.bool,
  }),
};

export default DualFileUploadField;
